// React Dependencies
import React, { useState } from "react";

// MUI Imports //
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Component Imports
import Loading from "./../../../Components/Interactive/Loading";
import TeaserResultTableBody from "./TeaserResultTableBody";
import ResultTableBodyProperty from "./ResultTableBodyProperty";
import OptOutSearchForm from "./OptOutSearchForm";
import OptOutSearchFormPhone from "./OptOutSearchFormPhone";
import OptOutSearchFormProperty from "./OptOutSearchFormProperty";
import OptOutSearchFormSocial from "./OptOutSearchFormSocial";

// css imports
import "./OptOutStyles.css"

const OptOut = ({ isOpen }) => {
  const [teaserResults, setTeaserResult] = useState(null);
  const [propertyResult, setPropertyResult] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setIsLoading] = useState(false);
  const [tab, setTab] = useState(1);

  const handleTeaserSearch = async (
    reportToken,
    brandId,
    firstName,
    lastName,
    city,
    state,
    minAge,
    maxAge
  ) => {
    setIsLoading(true);
    setError(null);
    setTeaserResult(null);
  
    if(!brandId)
    {
      setError("Please select a Brand");
      setIsLoading(false);
      return;
    }

    setBrandId(brandId);

    let endpoint;
    if(reportToken)
    {
      endpoint = `/api/opt/teaser?ReportToken=${reportToken}&BrandId=${brandId}`;
    }
    else if(firstName && lastName && state)
    {   
      endpoint = `/api/opt/teaser?BrandId=${brandId}&FirstName=${firstName}&LastName=${lastName}&City=${city}&MinAge=${minAge}&MaxAge=${maxAge}`;

      if(state !== "all")
      {
        endpoint += `&state=${state}`;
      }
    }
    else
    {
      setError("Invalid search input. Search by a report token or by a name and state");
      setIsLoading(false);
      return;
    }

    const res = await fetch(endpoint, {
      method: "GET",
      credentials: "include",
    });
    const { result, success } = await res.json();

    // If token does not exist
    if (result?.errorMessage) {
      setError(result.errorMessage);
      setIsLoading(false);
      setTimeout(() => {
        setError(null);
        setTeaserResult(null);
      }, 5000);
      return;
    }
    // If the token is incorrect
    else if (!success) {
      setError("Failed to send teaser search");
      setIsLoading(false);
      setTimeout(() => {
        setError(null);
        setTeaserResult(null);
      }, 5000);
      return;
    } else {
      setIsLoading(false);
      setTeaserResult(result.teaserReports);
    }
  };

  const handlePropertySearch = async (
    brandId,
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
  ) => {
    setIsLoading(true);
    setError(null);
    setPropertyResult(null);

    if(!brandId) {
      setError("Please Select a Brand");
      setIsLoading(false);
      return;
    }

    if (!addressLine1) {
      setError("Please Enter Address Line 1.");
      setIsLoading(false);
      return;
    }
    if (!city) {
      setError("Please Enter a City.");
      setIsLoading(false);
      return;
    }
    if (!state) {
      setError("Please Enter a State.");
      setIsLoading(false);
      return;
    }

    if (!zip) {
      setError("Please Enter a Zip Code.");
      setIsLoading(false);
      return;
    }

    setBrandId(brandId)

    let endPoint = `/api/opt/propertySearch?AddressLine1=${addressLine1}&AddressLine2=${addressLine2}&City=${city}&State=${state}&Zip=${zip}&BrandId=${brandId}`

    const res = await fetch(endPoint, {
      method: "GET",
      credentials: "include",
    });
    const { result, success } = await res.json();

    if (result?.errorMessage) {
      setError(result.errorMessage);
      setIsLoading(false);
      setTimeout(() => {
        setError(null);
        setPropertyResult(null);
      }, 5000);
      return;
    }
    else if (!success) {
      setError("Failed to send property search");
      setIsLoading(false);
      setTimeout(() => {
        setError(null);
        setPropertyResult(null);
      }, 5000);
      return;
    } else {
      setIsLoading(false);
      setPropertyResult(result.report.scalableReport);
    }
  }

  const handlePhoneOptOut = async (phoneNumber, brandId) => {

    setIsLoading(true);
    setError(null);

    if(!brandId)
    {
      setError("Please select a Brand");
      setIsLoading(false);
      return;
    }

    setBrandId(brandId);


    if (phoneNumber.length !== 16) {
      setError ("Valid phone number is required. Enter addition digits.");
      setIsLoading(false);
      return;
    }

    const res = await fetch(`/api/opt/phone-opt-out`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ 
        brandId: brandId,
        phone: phoneNumber,
      })
    });
    

    const { success } = await res.json();
    if (success) {
      alert("Opt out was successful!");
    } else {
      alert("Opt out failed!");
    }

  }

  const handleSocialOptOut = async (socialToken, brandId) => {

    setIsLoading(true);
    setError(null);

    if(!brandId)
    {
      setError("Please select a Brand");
      setIsLoading(false);
      return;
    }
    setBrandId(brandId);

    // remove leading @
    if (/^@/.test(socialToken)) {
      socialToken = socialToken.slice(1);
    }

    const res = await fetch(`/api/opt/social-opt-out`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ 
        brandId: brandId,
        socialToken: socialToken.trim(),
      })
    });
    

    const { success } = await res.json();
    if (success) {
      alert("Opt out was successful!");
    } else {
      alert("Opt out failed!");
    }

  }

  const changeTab = (e) => {
    e.preventDefault();
    let tab = e.target.innerText;

    switch (tab) {
      case "People":
        setTab(1)
        break;
      case "Phone":
        setTab(2)
        break;
      case "Property":
        setTab(3)
        break;
      case "Social":
        setTab(4)
        break;
      default:
          console.log("switch has unknown case");
    }
  }

  const ifLoading = () => {
    if (loading) {
      return <div className="loading-widget">
          <Loading />
        </div>
    } else if (tab === 1) {
      return (
          <TableContainer
              className="customer-order-table"
            >
              <Table aria-label="collapsible table" style={{ width: "100%" }}>
                <TableHead
                  style={{
                    backgroundColor: "#FDFAF6",
                    borderRadius: "10px !important",
                  }}
                >
                  <TableRow
                    style={{
                      backgroundColor: "#0D2B41",
                    }}
                  >
                    <TableCell align="center" className="headerText">
                      Name
                    </TableCell>
                    <TableCell align="center" className="headerText">
                      Report Token
                    </TableCell>
                    <TableCell align="center" className="headerText">
                      Age
                    </TableCell>
                    <TableCell align="center" className="headerText">
                      City
                    </TableCell>
                    <TableCell align="center" className="headerText">
                      State
                    </TableCell>
                    <TableCell align="center" className="headerText">
                      Relatives
                    </TableCell>
                    <TableCell align="center" className="headerText">
                      Action
                    </TableCell>                  
                  </TableRow>
                </TableHead>
                <TeaserResultTableBody
                  brandId={brandId}
                  teaserData={teaserResults}
                /> 
              </Table>
              <div className="bottom-footer"></div>
          </TableContainer>
      ) 
    } else if (tab === 3) {
          return (
            <TableContainer
                className="customer-order-table"
              >
                <Table aria-label="collapsible table" style={{ width: "100%" }}>
                  <TableHead
                    style={{
                      backgroundColor: "#FDFAF6",
                      borderRadius: "10px !important",
                    }}
                  >
                    <TableRow
                      style={{
                        backgroundColor: "#0D2B41",
                      }}
                    >
                      <TableCell align="center" className="headerText">
                        Full Address
                      </TableCell>
                      <TableCell align="center" className="headerText">
                      City
                      </TableCell>
                      <TableCell align="center" className="headerText">
                        State
                      </TableCell>
                      <TableCell align="center" className="headerText">
                        Zip
                      </TableCell>
                      <TableCell align="center" className="headerText">
                        Action
                      </TableCell>                  
                    </TableRow>
                  </TableHead>
                  <ResultTableBodyProperty
                    brandId={brandId}
                    propertyResult = {propertyResult}
                  /> 
                </Table>
                <div className="bottom-footer"></div>
            </TableContainer>
        )
    }

  }

  return (
    <div className={`${isOpen ? "page" : "page-closed"} `}>
      <h3 className="heading">Opt Out</h3>
      <div className="tabs-container">
        <button className= {tab === 1 ? "tab selected" : "tab"} onClick={changeTab}>People</button>
        <button className={tab === 2 ? "tab selected" : "tab"} onClick={changeTab}>Phone</button>
        <button className={tab === 3 ? "tab selected" : "tab"} onClick={changeTab}>Property</button>
        <button className={tab === 4 ? "tab selected" : "tab"} onClick={changeTab}>Social</button>
      </div>

      {tab === 1 ? (
        <div>
          <OptOutSearchForm 
            onTeaserSearchClick={handleTeaserSearch}
          /> 
          <div className="outer-wrapper">{ ifLoading() }</div>
        </div>
        ): (
          <div></div>
        )
      }
      {tab === 2 ? (
          <OptOutSearchFormPhone
            handlePhoneOptOut={handlePhoneOptOut}
          /> 
        ): (
          <div></div>
        )
      }
      {tab === 3 ? (
        <div>
          <OptOutSearchFormProperty
            handlePropertySearch={handlePropertySearch}
          /> 
          <div className="outer-wrapper">{ ifLoading() }</div>
        </div>
        ): (
          <div></div>
        )
      }
      {tab === 4 ? (
          <OptOutSearchFormSocial
            socialSubmit={handleSocialOptOut}
          /> 
        ): (
          <div></div>
        )
      }

      {error && (
        <h3
          className="heading"
          style={{ fontSize: "1.5rem", color: "#ff7575" }}
        >
          {error}
        </h3>
      )}
    </div>
  );
};

export default OptOut;
